import { all, call, delay, fork, put, select, take, takeEvery, takeLeading } from 'redux-saga/effects';

import { apiSlice, getApiAccessToken, getQueryErrorStatus, QueryErrorStatus, QueryPayloadAction, setSelectedTenant as setSelectedTenantApiHeader } from '@client/api';

import {
  appFailed,
  AppState,
  AppStatus,
  createAppError,
  getAppState,
  initApp,
  initAppFailed,
  initAppSucceeded,
  logout,
} from './app.slice';

import { config } from '../../config';
import { clearSession, getSessionState, SessionState, setSession, setSelectedTenant } from '../session/session.slice';

const logger = (msg: string, ...params: unknown[]) => {
  console.debug('[APP INIT]', msg, ...params);
};

// handler to init app step by step
function* handleInitApp() {
  let internalErrorMessage: string | undefined;

  try {
    logger('🚀 Init application started');
    logger('🔒 Init authorization process');

    logger('⏳ Checking for access token from Clerk');
    // Wait for ClerkTokenBridge to dispatch the token
    // The token is automatically managed by ClerkTokenBridge component
    let accessToken: string | null = yield call(getApiAccessToken);

    logger('🔎 Checking if access token exists');
    // Checks if the `accessToken` is set which is used & necessary for the `apiSlice` on every request
    if (accessToken == null) {
      // Token not yet available, wait a bit for Clerk to load
      yield delay(500);
      accessToken = yield call(getApiAccessToken);
      if (accessToken == null) {
        internalErrorMessage = 'Could not get access token from Clerk - Access denied';
        throw Error('You are not authenticated');
      }
    }

    logger('👤 Get session user');
    // handling of rejected (error) is done in another handler
    yield put(apiSlice.endpoints.getAuthSession.initiate() as never);

    // wait for the result of the call
    yield take([apiSlice.endpoints.getAuthSession.matchFulfilled, apiSlice.endpoints.getAuthSession.matchRejected]);

    // wait for the session user to be set as it will be needed in the following
    yield take(setSession.type);

    const sessionState: SessionState = yield select(getSessionState);

    // Use selectedTenant from API response (already set by setSession reducer)
    // If not in API response, fall back to localStorage
    if (!sessionState.selectedTenant) {
      const selectedTenant = window.localStorage.getItem('exio.selectedTenant');
      if (selectedTenant) {
        yield put(setSelectedTenant(JSON.parse(selectedTenant)));
      }
    }

    // Auto-select the only tenant on first login (no tenant in API response or localStorage)
    const updatedState: SessionState = yield select(getSessionState);
    if (!updatedState.selectedTenant && updatedState.allowedTenants.length === 1) {
      const onlyTenant = updatedState.allowedTenants[0];
      logger('🏢 Auto-selecting single tenant:', onlyTenant.name);
      setSelectedTenantApiHeader(onlyTenant.id);
      window.localStorage.setItem('exio.selectedTenant', JSON.stringify(onlyTenant));
      yield put(setSelectedTenant(onlyTenant));
    }

    const { isAuthenticated, user } = sessionState;

    if (!isAuthenticated) {
      internalErrorMessage = 'Session is not set to authenticated - Access denied';
      throw Error('You are not authenticated');
    }

    if (user == null) {
      internalErrorMessage = 'Could not find a session user - Access denied';
      throw Error('You are not authorized');
    }

    logger('🔓 Authorization completed');

    logger('✅ Init application succeeded');
    yield put(initAppSucceeded());
  } catch (e) {
    logger(`⛔️ ${internalErrorMessage}`);
    logger('❌ Init application failed');
    yield put(initAppFailed(createAppError(e)));
    return;
  }
}

// handler if init app failed
function* handleInitAppFailed() {
  if (config.stage === 'local') return;
  yield delay(10000);
  yield put(logout());
}

// handler if app failed
function* handleAppFailed() {
  if (config.stage === 'local') return;
  yield delay(10000);
  yield put(logout());
}

// handler for unable to receive session user & api permission from backend api
function* handleGetApiAuthSessionRejected(action: QueryPayloadAction) {
  const appState: AppState = yield select(getAppState);
  const status: QueryErrorStatus = yield call(getQueryErrorStatus, action);

  // If session returns 404 (user has no tenant/ACL yet), check for pending onboarding invitation
  if (status === 404 && appState.status === AppStatus.INIT) {
    logger('🔍 Session not found — checking for pending onboarding invitation');
    try {
      yield put(apiSlice.endpoints.getPendingPortfolioInvitation.initiate() as never);
      const invitationAction: { payload?: { invitationId?: string } } = yield take([
        apiSlice.endpoints.getPendingPortfolioInvitation.matchFulfilled,
        apiSlice.endpoints.getPendingPortfolioInvitation.matchRejected,
      ]);

      if (
        apiSlice.endpoints.getPendingPortfolioInvitation.matchFulfilled(invitationAction as never) &&
        invitationAction.payload?.invitationId
      ) {
        logger('📋 Found pending onboarding invitation — redirecting to onboarding');
        window.location.href = '/onboarding/portfolio';
        return;
      }
    } catch (e) {
      logger('⚠️ Failed to check for onboarding invitation', e);
    }
  }

  const message = 'You are not authorized';

  // if fetching the session user from the backend api fails while the app is in INIT state then we trigger `initAppFailed`
  if (appState.status === AppStatus.INIT) {
    logger('⛔️ Could not get session user - Access denied');
    yield put(initAppFailed(message));
  } else {
    yield put(appFailed(message));
  }
}

function* handleRtkQueryRejected(action: QueryPayloadAction) {
  // ignore `getAuthSession` endpoint error as it will be handled in a different handler
  if (action.meta.arg?.endpointName === apiSlice.endpoints.getAuthSession.name) return;

  const status: QueryErrorStatus = yield call(getQueryErrorStatus, action);

  // set app to error state when unauthorized
  if (status === 401) {
    yield put(appFailed('You are not authorized anymore - Please login again'));
  }
}

function* handleLogout() {
  try {
    if (window?.localStorage && window.localStorage.getItem('exio.selectedTenant')) {
      window.localStorage.removeItem('exio.selectedTenant');
    }
    yield all([put(clearSession()), put(apiSlice.util.resetApiState())]);
  } catch (e) {
    /** empty */
  }

  // Sign out from Clerk
  try {
    // Import Clerk dynamically to sign out
    const { useClerk } = yield import('@clerk/react');
    // Note: We can't use hooks in sagas, so we need to access Clerk instance directly
    // The ClerkTokenBridge will handle clearing the token from Redux when user signs out
    if (window.Clerk) {
      yield call([window.Clerk, window.Clerk.signOut]);
    } else {
      // Fallback: reload the page to trigger Clerk's sign-out
      window.location.reload();
    }
  } catch (e) {
    // If Clerk sign-out fails, reload the page
    window.location.reload();
  }
}

function* watchInitApp() {
  yield takeLeading(initApp.type, handleInitApp);
}

function* watchInitAppFailed() {
  yield takeEvery(initAppFailed.type, handleInitAppFailed);
}

function* watchAppFailed() {
  yield takeEvery(initAppFailed.type, handleAppFailed);
}

function* watchGetApiSessionRejected() {
  const matcher = apiSlice.endpoints.getAuthSession?.matchRejected;
  if (!matcher) return;
  yield takeEvery(matcher, handleGetApiAuthSessionRejected);
}

function* watchRtkQueryRejected() {
  yield takeEvery(
    [`${apiSlice.reducerPath}/executeQuery/rejected`, `${apiSlice.reducerPath}/executeMutation/rejected`],
    handleRtkQueryRejected,
  );
}

function* watchLogout() {
  yield takeLeading(logout, handleLogout);
}

function* startAppWatchers() {
  yield fork(watchInitApp);
  yield fork(watchInitAppFailed);
  yield fork(watchAppFailed);
  yield fork(watchGetApiSessionRejected);
  yield fork(watchRtkQueryRejected);
  yield fork(watchLogout);
}

export function* appSaga() {
  yield fork(startAppWatchers);
}
